/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { sigintCreateSensor } from "../funcs/sigintCreateSensor.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SigintSensor } from "../models/components/sigintsensor.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateSigintSensorRequest,
  CreateSigintSensorSecurity,
} from "../models/operations/createsigintsensor.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateSigintSensorMutationVariables = {
  request: CreateSigintSensorRequest;
  security?: CreateSigintSensorSecurity | undefined;
  options?: RequestOptions;
};

export type CreateSigintSensorMutationData = SigintSensor;

export type CreateSigintSensorMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createSensor sigint
 *
 * @remarks
 * Create a sensor. multi_label permits independently applicable labels, exclusive makes attached signals compete and permits at most 255, and ordered_score uses ordered levels and permits at most 10. Empty and one-signal sensors are valid drafts. Omitted signal_ids creates an empty sensor; [] is explicitly empty. Repeated, deleted, missing, or cross-project signal IDs are rejected atomically.
 */
export function useCreateSigintSensorMutation(
  options?: MutationHookOptions<
    CreateSigintSensorMutationData,
    CreateSigintSensorMutationError,
    CreateSigintSensorMutationVariables
  >,
): UseMutationResult<
  CreateSigintSensorMutationData,
  CreateSigintSensorMutationError,
  CreateSigintSensorMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateSigintSensorMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateSigintSensor(): MutationKey {
  return ["@gram/client", "sigint", "createSensor"];
}

export function buildCreateSigintSensorMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateSigintSensorMutationVariables,
  ) => Promise<CreateSigintSensorMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateSigintSensor(),
    mutationFn: function createSigintSensorMutationFn({
      request,
      security,
      options,
    }): Promise<CreateSigintSensorMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(sigintCreateSensor(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
